import React, { createContext, useContext, useEffect, useRef } from "react"
import { ISearchResultsDataItemResponseModel, ISearchResultsListContextPropsModel } from "../core/_models"
import MyAlert from "@common-utils/MyAlert"
import MyCard from "@common-utils/MyCard"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyPagination from "@common-utils/MyPagination"
import BulkSelectionContainerForListings from "../../../../../../utils/BulkSelectionContainerForListings"
import CandidatsRechercheResultsListItem from "./_CandidatsRechercheResultsListItem"
import { useSearchResultsContext } from "../CandidatsRechercheResults"
import { CONST_DEFAULT_TOAST_OPTIONS } from "@common-constants/*"
import { toast } from "react-toastify"
import MyTooltip from "@common-utils/MyTooltip"

// List context
const SearchResultsListContext = createContext<ISearchResultsListContextPropsModel>({} as ISearchResultsListContextPropsModel)
export const useSearchResultsListContext = () => useContext(SearchResultsListContext)
const CandidatsRechercheResultsList = () => {
     const context = useSearchResultsContext()
     const selectedItemRef = useRef<any>(null)

     // BULK SELECTION: handling the "check all elements" button click
     function handleCheckAllElementsInPageCallBack() {
          const items = context.listingQuery.data!.data
          context.setIsBulkSelectionEnabled(prev => ({
               ...prev,
               checkedElementsInPage: items,
          }))
          toast.info("Tous les éléments de cette page ont été cochés.", { ...CONST_DEFAULT_TOAST_OPTIONS, autoClose: 1000 })
     }

     // BULK SELECTION: handling the "check all elements across all pages" button click
     function handleCheckAllElementsAcrossAllPagesCallBack() {
          context.setIsBulkSelectionEnabled(prev => ({
               ...prev,
               areAllElementsInAllPagesChecked: true,
          }))
          toast.info("Tous les éléments correspondant à votre recherche ont été cochés.", { ...CONST_DEFAULT_TOAST_OPTIONS, autoClose: 1000 })
     }

     // BULK SELECTION: handling the "Quit" button click
     function handleQuitBulkSelectionModeCallBack() {
          context.setIsBulkSelectionEnabled({
               enabled: false,
               checkedElementsInPage: [],
               areAllElementsInAllPagesChecked: false,
          })
     }

     useEffect(() => {
          if (selectedItemRef.current) {
               if (window.innerWidth >= 1200) {
                    /**
                     * THIS BEHAVIOR IS ONLY APPLICABLE ON DISPLAYS OF SIZE XL OR LARGER (it will not work for mobile because we didn't find a solution yet)
                     * This will cause the list to scroll and bring the selected item into focus.
                     * For example, as you navigate from one item to another, reaching the last visible item in the list will trigger a downward scroll to reveal the next item.
                     */
                    selectedItemRef.current.scrollIntoView({
                         block: "nearest",
                         inline: "nearest",
                         behavior: "smooth",
                    })
               } else {
                    // On <= display, this will focus the preview container when an item is clicked
                    context.previewContainerRef.current.scrollIntoView({
                         behavior: "smooth",
                    })
               }
          }
     }, [context.itemInPreview])

     return (
          <SearchResultsListContext.Provider value={{ selectedItemRef }}>
               <MyCard>
                    <MyCard.Header
                         isCollapseIconShown={false}
                         rightSideContent={
                              <>
                                   {context.listingQuery.isFetching ? (
                                        <MySimpleSpinner size={"sm"} />
                                   ) : (
                                        <>
                                             {context.listingQuery.data && context.listingQuery.data.totalElementsInPage > 0 && (
                                                  <div className={"d-flex align-items-center"}>
                                                       {/* Pagination component*/}
                                                       <MyPagination
                                                            page={context.filters.page as number}
                                                            pageStart={context.listingQuery.data.pageStart}
                                                            pageEnd={context.listingQuery.data.pageEnd}
                                                            totalPages={context.listingQuery.data.totalPages}
                                                            totalElementsInAllPages={context.listingQuery.data.totalElementsInAllPages}
                                                            onPreviousPageCallBack={() => {
                                                                 context.setFilters(prev => ({
                                                                      ...prev,
                                                                      page: (prev.page as number) - 1,
                                                                 }))
                                                            }}
                                                            onNextPageCallBack={() => {
                                                                 context.setFilters(prev => ({
                                                                      ...prev,
                                                                      page: (prev.page as number) + 1,
                                                                 }))
                                                            }}
                                                       />
                                                  </div>
                                             )}
                                        </>
                                   )}
                              </>
                         }
                    >
                         Candidats
                    </MyCard.Header>

                    <MyCard.Body noPadding={true}>
                         <div id={"list-items"}>
                              {/* Fetching */}
                              {context.listingQuery.isFetching ? (
                                   <div className={"text-center p-5"}>
                                        <MySimpleSpinner classNames={"ms-2"} />
                                   </div>
                              ) : (
                                   <>
                                        {/* In case of error */}
                                        {context.listingQuery.error ? (
                                             <MyAlert type={"danger"}>{context.listingQuery.error.response?.data.detail}</MyAlert>
                                        ) : (
                                             <>
                                                  {/* CASE: no data */}
                                                  {context.listingQuery.data && context.listingQuery.data.data.length === 0 && (
                                                       <div className="text-center text-muted p-4 fs-4">Aucun résultat</div>
                                                  )}

                                                  {/* CASE: If some data */}
                                                  {context.listingQuery.data && context.listingQuery.data.data.length > 0 && (
                                                       <>
                                                            {/* Mass selection actions component */}
                                                            {context.isBulkSelectionEnabled.enabled && (
                                                                 <BulkSelectionContainerForListings
                                                                      areAllElementsInAllPagesChecked={context.isBulkSelectionEnabled.areAllElementsInAllPagesChecked}
                                                                      checkedElementIdsInPage={context.isBulkSelectionEnabled.checkedElementsInPage}
                                                                      totalElementsInPage={context.listingQuery.data.totalElementsInPage}
                                                                      totalElementsInAllPages={context.listingQuery.data.totalElementsInAllPages}
                                                                      allowCheckAllElementInAllPages={false}
                                                                      handleCheckAllElementsInPageCallBack={handleCheckAllElementsInPageCallBack}
                                                                      handleCheckAllElementsAcrossAllPagesCallBack={handleCheckAllElementsAcrossAllPagesCallBack}
                                                                      handleQuitBulkSelectionModeCallBack={handleQuitBulkSelectionModeCallBack}
                                                                      AllOrFewElementsCheckedInCurrentPageActionsChildren={
                                                                           <div className={"d-flex justify-content-center align-items-center gap-2"}>
                                                                                {context.filters.filters?.viviers && (
                                                                                     <MyTooltip
                                                                                          title={`Supprimer ${
                                                                                               context.filters.filters?.viviers.length > 0 ? "des viviers" : "du vivier"
                                                                                          }`}
                                                                                          placement={"bottom"}
                                                                                     >
                                                                                          <button
                                                                                               type={"button"}
                                                                                               className={`btn btn-outline btn-outline-danger btn-sm btn-icon rounded-circle`}
                                                                                               onClick={() =>
                                                                                                    context.handleRemoveFromViviers(
                                                                                                         context.isBulkSelectionEnabled.checkedElementsInPage,
                                                                                                         context.filters.filters!.viviers!
                                                                                                    )
                                                                                               }
                                                                                          >
                                                                                               <i className="fas fa-times fs-4"></i>
                                                                                          </button>
                                                                                     </MyTooltip>
                                                                                )}

                                                                                <MyTooltip title={"Ajouter à un vivier"} placement={"bottom"}>
                                                                                     <button
                                                                                          type={"button"}
                                                                                          className={`btn btn-outline btn-outline-danger btn-sm btn-icon rounded-circle`}
                                                                                          onClick={() =>
                                                                                               context.setAddingCandidatesToViviers(
                                                                                                    context.isBulkSelectionEnabled.checkedElementsInPage
                                                                                               )
                                                                                          }
                                                                                     >
                                                                                          <i className="fas fa-heart fs-4"></i>
                                                                                     </button>
                                                                                </MyTooltip>

                                                                                <MyTooltip title={"Ajouter à ma liste de traitement"} placement={"bottom"}>
                                                                                     <button
                                                                                          type={"button"}
                                                                                          className={`btn btn-outline btn-outline-warning btn-sm btn-icon rounded-circle`}
                                                                                          onClick={() =>
                                                                                               context.handleAddToListeTraitement(
                                                                                                    context.isBulkSelectionEnabled.checkedElementsInPage
                                                                                               )
                                                                                          }
                                                                                     >
                                                                                          <i className="fas fa-plus fs-4"></i>
                                                                                     </button>
                                                                                </MyTooltip>

                                                                                <MyTooltip title={"Supprimer de ma liste de traitement"} placement={"bottom"}>
                                                                                     <button
                                                                                          type={"button"}
                                                                                          className={`btn btn-warning btn-sm btn-icon rounded-circle`}
                                                                                          onClick={() =>
                                                                                               context.handleRemoveFromListeTraitement(
                                                                                                    context.isBulkSelectionEnabled.checkedElementsInPage
                                                                                               )
                                                                                          }
                                                                                     >
                                                                                          <i className="fas fa-minus fs-4"></i>
                                                                                     </button>
                                                                                </MyTooltip>
                                                                           </div>
                                                                      }
                                                                 />
                                                            )}
                                                            {/* List elements */}
                                                            <div
                                                                 // className={"hover-scroll-overlay-y"}
                                                                 style={{
                                                                      maxHeight: context.isBulkSelectionEnabled.enabled ? "59vh" : "68vh",
                                                                      overflowY: "auto",
                                                                 }}
                                                            >
                                                                 {context.listingQuery.data.data.map((item: ISearchResultsDataItemResponseModel, index) => (
                                                                      <div className="border border-bottom" key={item.candidat.id}>
                                                                           <CandidatsRechercheResultsListItem item={item} index={index} />
                                                                      </div>
                                                                 ))}
                                                            </div>
                                                       </>
                                                  )}
                                             </>
                                        )}
                                   </>
                              )}
                         </div>
                    </MyCard.Body>
               </MyCard>
          </SearchResultsListContext.Provider>
     )
}

export default CandidatsRechercheResultsList
